import React, { useMemo } from 'react';
import { useTranslation } from '@stateManagement/TranslationContext';
import { PageHelmet } from '@components/PageHelmet';
import { ArticleSectionPageHelmet } from '@components/BlogSection/ArticleSectionPageHelmet';
import { getTranslation } from '@helpers/languageTranslations';
import { cleanEscapes } from '@helpers/string';
import { getBlogStructuredDataScript } from '@helpers/articleScriptHelper';
import { useStaticValues } from '@stateManagement/StaticValuesContext';

interface BlogHOCProps {
    type?: 'authorPage' | 'articlePage' | 'categoryPage';
    noIndex?: boolean;
    children: React.ReactNode;
}

export function BlogHOC({ type, noIndex, children }: BlogHOCProps): JSX.Element {
    const { prefixStringWithBaseUrl, blog, mediaUrlPrefix, fullUrl } = useStaticValues();
    const cleanFullUrl = cleanEscapes(fullUrl, '"');
    const { selectedLanguageTranslation } = useTranslation();
    const categoriesTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'categories'),
        [selectedLanguageTranslation]
    );

    const pageHelmet = useMemo(() => {
        switch (type) {
            case 'authorPage':
                return (
                    <ArticleSectionPageHelmet
                        isAuthorPage={true}
                        article={blog}
                        articleType='blogArticle'
                        noIndex={noIndex}
                        authorTranslation={categoriesTranslation.geotabAuthor}
                    />
                );
            case 'articlePage':
                return (
                    <ArticleSectionPageHelmet
                        isAuthorPage={false}
                        article={blog}
                        articleType='blogArticle'
                        noIndex={noIndex}
                        script={getBlogStructuredDataScript(
                            blog.articleData?.blogArticle,
                            mediaUrlPrefix,
                            prefixStringWithBaseUrl,
                            cleanFullUrl
                        )}
                    />
                );
            case 'categoryPage':
                return (
                    <PageHelmet
                        seo={{
                            title: `${blog.categoryPageData?.category.title || blog.categoryPageData?.category.name} ${
                                categoriesTranslation.page
                            } ${blog.categoryPageData?.pagedBlogArticles.currentPage} | ${
                                categoriesTranslation.fleetManagementBlog
                            }`,
                            description: blog.categoryPageData?.category.description,
                            canonical: blog.categoryPageData?.category.canonical,
                            datePublished: blog.categoryPageData?.category.publishedDateUtc,
                            dateModified: blog.categoryPageData?.category.modifiedDateUtc,
                        }}
                        noIndex={noIndex}
                    />
                );
            default:
                return <PageHelmet seo={getTranslation(selectedLanguageTranslation, 'blogHome', 'seo')} />;
        }
    }, [
        blog,
        noIndex,
        type,
        selectedLanguageTranslation,
        cleanFullUrl,
        mediaUrlPrefix,
        prefixStringWithBaseUrl,
        categoriesTranslation,
    ]);

    return (
        <>
            {pageHelmet}
            <div id='blogMainContent'>{children}</div>
        </>
    );
}
